import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronDown, ArrowLeft, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ConversionBlock from "@/components/ConversionBlock";
import { SERVICES } from "@/data/site";

function FAQ({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`faq-${idx}`} style={{ borderBottom: "1px solid var(--line)" }}>
      <button
        onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{
          width: "100%", background: "transparent", border: "none",
          padding: "22px 0", textAlign: "left",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          cursor: "pointer", fontFamily: "Manrope, sans-serif",
          fontWeight: 500, fontSize: 16, color: "var(--text)",
          letterSpacing: "-0.012em",
        }}
      >
        <span>{q}</span>
        <ChevronDown
          size={18} color="var(--gold)"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 200ms ease", flexShrink: 0, marginLeft: 16 }}
        />
      </button>
      {open && (
        <div className="body" style={{ paddingBottom: 22, maxWidth: 760 }}>
          {a}
        </div>
      )}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) document.title = `${service.title} — Totál-Bádogos Kft.`;
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  return (
    <div data-testid={`service-page-${slug}`} style={{ background: "var(--ink)" }}>
      <Nav />
      <main>
        {/* Hero */}
        <section style={{ background: "var(--ink)", paddingTop: 140, paddingBottom: 80 }}>
          <div className="site-container">
            <Link to="/" data-testid="service-back" className="small"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                color: "var(--text-dim)", textDecoration: "none",
                textTransform: "uppercase", letterSpacing: "0.12em",
                marginBottom: 32,
              }}
            >
              <ArrowLeft size={13} /> Vissza
            </Link>

            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
              <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
              <span className="eyebrow">{service.number} — Szakterület</span>
            </div>

            <h1 className="h1" data-testid="service-h1" style={{ margin: 0, maxWidth: 920 }}>
              {service.title}
            </h1>
            <p className="lead" style={{ marginTop: 28, maxWidth: 620, fontSize: 16 }}>
              {service.long}
            </p>
            <div style={{ marginTop: 36, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <a href="/#kapcsolat" className="btn btn-gold" data-testid="service-cta">
                Ingyenes felmérés <ArrowRight size={14} />
              </a>
              <a href="/#referenciak" className="btn btn-ghost">
                Referenciák
              </a>
            </div>
          </div>
        </section>

        {/* Scope */}
        <section className="section" style={{ background: "var(--ink-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
          <div className="site-container">
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
              <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
              <span className="eyebrow">Munka tartalma</span>
            </div>
            <h2 className="h2" style={{ margin: 0, marginBottom: 40, maxWidth: 720 }}>
              Mindegyik {service.title.toLowerCase()} <span style={{ color: "var(--gold)" }}>tartalmazza.</span>
            </h2>
            <ul style={{
              listStyle: "none", padding: 0, margin: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 0,
              border: "1px solid var(--line)",
            }}>
              {service.scope.map((item, i) => (
                <li
                  key={i} data-testid={`scope-${i}`}
                  style={{
                    background: "var(--ink)",
                    padding: "22px 24px",
                    display: "flex", alignItems: "flex-start", gap: 16,
                    fontSize: 14, color: "var(--text)", lineHeight: 1.55,
                    borderRight: "1px solid var(--line)",
                    borderBottom: "1px solid var(--line)",
                  }}
                >
                  <span className="font-display" style={{ fontSize: 13, color: "var(--gold)", letterSpacing: "0.04em", minWidth: 24 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" style={{ background: "var(--ink)" }}>
          <div className="site-container">
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
              <span style={{ height: 1, background: "var(--gold)", width: 32 }} />
              <span className="eyebrow">Gyakori kérdések</span>
            </div>
            <h2 className="h2" style={{ margin: 0, marginBottom: 40 }}>
              Kérdések, amiket <span style={{ color: "var(--gold)" }}>valóban</span> kérdeznek.
            </h2>
            <div style={{ maxWidth: 880, borderTop: "1px solid var(--line)" }}>
              {service.faqs.map((f, i) => <FAQ key={i} idx={i} q={f.q} a={f.a} />)}
            </div>
          </div>
        </section>

        <ConversionBlock testId="service-cta-block" />
      </main>
      <Footer />
    </div>
  );
}
